import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { relationshipsApi } from '../relationships/api';
import { useOpenInviteLinkMutation } from './api';
import { clearPendingInviteLink, pendingInviteVia } from './pending';

/**
 * Once someone who arrived with an invite link is signed in with a handle,
 * open it: the pair starts exactly as if they had written to the member by
 * @handle, and they land on that conversation. Returns a notice when the link
 * could not be opened.
 */
export function useOpenInviteLink(token: string | null): [string | null, () => void] {
  const dispatch = useAppDispatch();
  const [open] = useOpenInviteLinkMutation();
  const [notice, setNotice] = useState<string | null>(null);
  const tried = useRef(false);

  useEffect(() => {
    if (!token || tried.current) return;
    tried.current = true;
    void open({ token, via: pendingInviteVia() })
      .unwrap()
      .then((rel) => {
        dispatch(relationshipsApi.util.invalidateTags(['Relationship']));
        window.location.hash = `#/c/${rel.id}`;
      })
      .catch((e: { status?: number }) => {
        setNotice(
          e.status === 409
            ? "That's your own invite link. Send it to someone you'd like to hear from."
            : e.status === 429
              ? // A lasting link that has started all its new pairs for today.
                'Lots of new post for them today. Try again tomorrow, or find them by their @handle.'
              : 'That invite link has closed. Ask whoever sent it for a fresh one.',
        );
      })
      .finally(clearPendingInviteLink);
  }, [token, open, dispatch]);

  return [notice, () => setNotice(null)];
}
